'use strict';

/**
 * Progressive insight model — never block user-facing output when any employee data exists.
 * Completeness affects depth and confidence labels, not whether insight is shown.
 */

const { computePayGapMetrics } = require('./employeeProfileMetrics');
const { isValidBenchmark } = require('./marketValidation');
const { primaryDriverLabel } = require('../risk-engine/managerUiLabels');
const { buildNaturalProfileSummary } = require('./profileSummaryNarrative');

const INSIGHT_TIER = Object.freeze({
  FULL: 'full',
  PARTIAL: 'partial',
  MINIMAL: 'minimal',
  NONE: 'none',
});

const HARD_BLOCK_PHRASE_RE =
  /^(not enough data available|not enough data|insufficient information|no information available|data unavailable)$/i;

const SOFT_BLOCK_PHRASE_RE =
  /no verified main driver|no main driver stands out yet|review individual factors for more detail/i;

function employeeHasUsableData(employee) {
  if (!employee || typeof employee !== 'object') return false;
  const fields = [
    employee.id,
    employee.name,
    employee.role,
    employee.department,
    employee.team,
    employee.salary,
    employee.marketSalary,
    employee.hireDate,
    employee.email,
    employee.workLocation,
    employee.location,
    employee.risk_score,
    employee.risk_score_payload,
    employee.marketComparison,
  ];
  return fields.some((v) => v != null && String(v).trim() !== '');
}

function isHardBlockPhrase(text) {
  const t = String(text || '').trim();
  if (!t) return true;
  if (HARD_BLOCK_PHRASE_RE.test(t)) return true;
  return false;
}

function isSoftBlockPhrase(text) {
  return SOFT_BLOCK_PHRASE_RE.test(String(text || ''));
}

/**
 * @param {object|null|undefined} employee
 * @param {object|null|undefined} turnover
 * @returns {number} 0–100
 */
function computeDataCompleteness(employee, turnover = null) {
  if (!employeeHasUsableData(employee)) return 0;

  let score = 0;
  const add = (n) => {
    score += n;
  };

  if (employee.name) add(8);
  if (employee.role) add(12);
  if (employee.department || employee.team) add(8);
  if (employee.hireDate) add(10);
  if (Number.isFinite(Number(employee.salary))) add(15);
  if (isValidBenchmark(employee.marketComparison)) add(22);
  else if (employee.workLocation || employee.location) add(6);

  const coverage = Number(turnover?.coverageScore);
  if (Number.isFinite(coverage)) {
    score += Math.min(25, Math.round(coverage * 0.25));
  }

  if (turnover?.riskExplanation?.primaryDriver?.detail) add(10);
  if (Array.isArray(turnover?.topContributors) && turnover.topContributors.length > 0) add(5);

  return Math.max(0, Math.min(100, score));
}

function completenessTier(completeness) {
  if (completeness >= 55) return INSIGHT_TIER.FULL;
  if (completeness >= 22) return INSIGHT_TIER.PARTIAL;
  if (completeness > 0) return INSIGHT_TIER.MINIMAL;
  return INSIGHT_TIER.NONE;
}

function roleContextPhrase(employee) {
  const role = employee?.role ? String(employee.role).trim() : '';
  const dept = employee?.department ? String(employee.department).trim() : '';
  const loc = employee?.workLocation || employee?.location;
  const locStr = loc ? String(loc).trim() : '';
  if (!role && !dept) return 'this employee';
  let phrase = role || 'this role';
  if (dept) phrase += ` on the ${dept} team`;
  if (locStr) phrase += ` in ${locStr}`;
  return phrase;
}

/**
 * Best grounded partial insight from whatever fields exist (no invented history).
 * @param {object} employee
 * @param {object|null} [turnover]
 * @param {object|null} [compBench]
 * @returns {{ text: string, completeness: number, tier: string, confidenceNote: string|null }}
 */
function buildPartialEmployeeInsight(employee, turnover = null, compBench = null) {
  const completeness = computeDataCompleteness(employee, turnover);
  const tier = completenessTier(completeness);
  const limitedNote =
    tier === INSIGHT_TIER.MINIMAL
      ? 'Insight is directional — several HR fields are still missing.'
      : tier === INSIGHT_TIER.PARTIAL
        ? 'Some HR fields are missing; this summary uses the strongest available signals.'
        : null;

  const re = turnover?.riskExplanation;
  if (
    Number.isFinite(Number(turnover?.score)) &&
    turnover?.scoreReady !== false &&
    re &&
    (re.primaryDriver?.detail || re.secondaryDrivers?.length)
  ) {
    const natural = buildNaturalProfileSummary({
      employee,
      score: turnover?.score,
      level: turnover?.level,
      primary: re.primaryDriver,
      secondary: re.secondaryDrivers,
      positiveSignals: re.positiveSignals,
    });
    if (natural) {
      return { text: natural, completeness, tier, confidenceNote: limitedNote };
    }
  }

  const parts = [];
  const ctx = roleContextPhrase(employee);

  const primaryDetail = turnover?.riskExplanation?.primaryDriver?.detail;
  if (primaryDetail && !isSoftBlockPhrase(primaryDetail) && !isHardBlockPhrase(primaryDetail)) {
    parts.push(String(primaryDetail).trim());
  } else if (turnover?.summary && !isHardBlockPhrase(turnover.summary)) {
    parts.push(String(turnover.summary).trim());
  }

  const gapMetrics = computePayGapMetrics(employee, compBench);
  if (gapMetrics.payGapPct != null && gapMetrics.payGapPct > 5) {
    parts.push(
      `Pay is about ${Math.round(gapMetrics.payGapPct)}% below market.`
    );
  } else if (gapMetrics.payGapPct != null && gapMetrics.payGapPct < -5) {
    parts.push(`Pay is above market, which can help retention.`);
  } else if (Number.isFinite(Number(employee.salary))) {
    parts.push(`Current compensation is on file; market comparison is limited.`);
  }

  const top = Array.isArray(turnover?.topContributors) ? turnover.topContributors[0] : null;
  if (top?.reason && !isSoftBlockPhrase(top.reason)) {
    const label = top.factorKey ? primaryDriverLabel(top.factorKey) : 'Retention signal';
    parts.push(`${label}: ${String(top.reason).replace(/\.$/, '')}.`);
  }

  if (parts.length === 0) {
    if (employee.role || employee.department) {
      parts.push(
        `Limited HR history is on file for ${ctx}. Role and compensation fields can still inform a directional retention read.`
      );
    } else if (employee.name) {
      parts.push(
        `${String(employee.name).trim()} has a sparse profile. Add role, pay, and tenure to sharpen risk insights.`
      );
    } else {
      parts.push('This profile has minimal fields on file. Add role and compensation to generate a sharper read.');
    }
  }

  const deduped = [];
  const seen = new Set();
  for (const p of parts) {
    const t = String(p).trim();
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    deduped.push(t);
  }

  return {
    text: deduped.slice(0, 3).join(' '),
    completeness,
    tier,
    confidenceNote: limitedNote,
  };
}

/**
 * Resolve user-facing copy: prefer explicit grounded text, else progressive partial insight.
 * @param {object} params
 * @returns {string}
 */
function resolveProgressiveInsight({
  employee = null,
  turnover = null,
  compBench = null,
  explicit = null,
  purpose = 'general',
}) {
  const explicitStr = explicit != null ? String(explicit).trim() : '';
  if (explicitStr && !isHardBlockPhrase(explicitStr) && !isSoftBlockPhrase(explicitStr)) {
    return explicitStr;
  }

  if (!employeeHasUsableData(employee)) {
    if (purpose === 'actions') {
      return 'Add role and compensation details to generate tailored recommendations.';
    }
    return 'Add employee profile details to generate insights.';
  }

  const partial = buildPartialEmployeeInsight(employee, turnover, compBench);
  if (purpose === 'pay_vs_market') {
    const { formatPayVsMarketLabel } = require('./formatPayVsMarket');
    const pay = computePayGapMetrics(employee, compBench);
    if (pay.available) {
      return formatPayVsMarketLabel({
        payGapDollars: pay.payGapDollars,
        payGapPct: pay.payGapPct,
      });
    }
    if (Number.isFinite(Number(employee.salary))) return 'Pay on file; market benchmark pending';
  }
  if (purpose === 'replacement_cost') {
    const est = Number(employee.estimatedReplacementCost);
    if (Number.isFinite(est) && est > 0) {
      return `Estimated ${est >= 1000 ? `~$${Math.round(est / 1000)}K` : `$${est}`} to replace`;
    }
    if (Number.isFinite(Number(employee.salary)) && employee.salary > 0) {
      return `Typically 50–200% of salary (${roleContextPhrase(employee)})`;
    }
  }
  if (purpose === 'risk_trend') {
    const temporal = turnover?.temporal;
    if (temporal?.narrative && !/insufficient/i.test(temporal.narrative)) {
      return String(temporal.narrative).split(/(?<=[.!?])\s+/)[0];
    }
    if (Number.isFinite(Number(turnover?.score))) {
      return 'Risk score available; trend builds as more history is recorded';
    }
  }
  if (purpose === 'top_risk_driver') {
    const primary = turnover?.riskExplanation?.primaryDriver?.detail;
    if (primary && !isSoftBlockPhrase(primary)) return primary;
    const firstReason = Array.isArray(turnover?.riskReasons) ? turnover.riskReasons[0] : null;
    if (firstReason && !isSoftBlockPhrase(firstReason)) return String(firstReason);
  }

  return partial.text;
}

/**
 * Progressive replacement for legacy NOT_ENOUGH_DATA fallback string.
 */
function progressiveFallbackLabel(employee, turnover = null, compBench = null, purpose = 'general') {
  return resolveProgressiveInsight({ employee, turnover, compBench, purpose });
}

module.exports = {
  INSIGHT_TIER,
  employeeHasUsableData,
  isHardBlockPhrase,
  isSoftBlockPhrase,
  computeDataCompleteness,
  completenessTier,
  buildPartialEmployeeInsight,
  resolveProgressiveInsight,
  progressiveFallbackLabel,
};
